import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../services/api";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');

.myorders-page {
  background: #f9f7f4;
  min-height: 100vh;
  font-family: 'DM Sans', sans-serif;
  padding: 40px 0 80px;
}
.myorders-inner {
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 24px;
}
.myorders-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 28px;
}

/* ─── Loading ─── */
.myorders-loading {
  text-align: center;
  padding: 80px 0;
  color: #8a8a8a;
}
.myorders-spinner {
  width: 36px; height: 36px;
  border: 3px solid #e8e4df;
  border-top-color: #1a5c38;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ─── Empty / guest info card ─── */
.myorders-empty {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8e4df;
  padding: 48px 32px;
  text-align: center;
  max-width: 560px;
  margin: 0 auto;
}
.myorders-empty-icon {
  font-size: 3rem;
  margin-bottom: 16px;
  display: block;
}
.myorders-empty h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
}
.myorders-empty p {
  font-size: 0.92rem;
  color: #6a6a6a;
  line-height: 1.6;
  margin-bottom: 8px;
}

/* ─── Guest info box ─── */
.guest-info-box {
  background: #f0f9f4;
  border: 1px solid #c5dece;
  border-radius: 14px;
  padding: 20px;
  margin: 24px 0;
  text-align: left;
}
.guest-info-title {
  font-weight: 700;
  color: #1a5c38;
  font-size: 0.95rem;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.guest-info-list {
  font-size: 0.87rem;
  color: #4a4a4a;
  line-height: 1.7;
  padding-left: 0;
  list-style: none;
}
.guest-info-list li {
  padding: 5px 0;
  display: flex;
  gap: 10px;
}
.guest-info-list li::before {
  content: '✓';
  color: #1a5c38;
  font-weight: 700;
  flex-shrink: 0;
}

/* ─── Action buttons ─── */
.myorders-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}
.btn-primary-green {
  background: #1a5c38;
  color: #fff;
  border: none;
  border-radius: 100px;
  padding: 13px 28px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: background 0.15s;
}
.btn-primary-green:hover { background: #134429; color: #fff; }
.btn-outline-green {
  background: transparent;
  color: #1a5c38;
  border: 2px solid #1a5c38;
  border-radius: 100px;
  padding: 11px 26px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: all 0.15s;
}
.btn-outline-green:hover { background: #1a5c38; color: #fff; }

/* ─── Order card ─── */
.order-card {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8e4df;
  margin-bottom: 20px;
  overflow: hidden;
}
.order-header {
  padding: 16px 24px;
  background: #fafaf9;
  border-bottom: 1px solid #e8e4df;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.order-id-label {
  font-size: 0.75rem;
  color: #8a8a8a;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-right: 8px;
}
.order-id-value {
  font-weight: 700;
  color: #1a1a1a;
  font-size: 0.95rem;
}
.order-badge {
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.badge-pending    { background: #fff5e6; color: #c47a1f; }
.badge-processing { background: #e8f3ed; color: #1a5c38; }
.badge-shipped    { background: #e8f0fc; color: #2c5aa8; }
.badge-delivered  { background: #e8f3ed; color: #1a5c38; }
.badge-cancelled  { background: #fef2f2; color: #d94f3d; }

.order-body { padding: 20px 24px; }
.order-products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}
.order-product {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f9f7f4;
  border-radius: 12px;
  border: 1px solid #e8e4df;
}
.order-product-img {
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
}
.order-product-info {
  flex: 1;
  min-width: 0;
}
.order-product-name {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1a1a1a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 4px;
}
.order-product-meta {
  font-size: 0.82rem;
  color: #6a6a6a;
}

.order-footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0ede9;
}
.order-meta-list { font-size: 0.85rem; color: #6a6a6a; }
.order-meta-list strong { color: #1a1a1a; font-weight: 600; }
.order-meta-list > div { padding: 4px 0; }
.order-summary { text-align: right; font-size: 0.85rem; color: #6a6a6a; }
.order-summary strong { color: #1a1a1a; font-weight: 600; }
.order-summary > div { padding: 4px 0; }
.order-total {
  font-family: 'Playfair Display', serif;
  font-size: 1.2rem !important;
  color: #1a5c38 !important;
  font-weight: 700;
  margin-top: 8px;
  padding-top: 10px !important;
  border-top: 2px solid #e8e4df;
}

.pay-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 6px;
}
.pay-paid    { background: #e8f3ed; color: #1a5c38; }
.pay-unpaid  { background: #fef2f2; color: #d94f3d; }
.pay-refunded{ background: #fff5e6; color: #c47a1f; }

/* ─── Delivery / feedback actions ─── */
.order-actions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0ede9;
  display: flex;
  justify-content: flex-end;
}
.btn-confirm {
  background: #1a5c38; color: #fff; border: none;
  border-radius: 100px; padding: 11px 22px;
  font-family: 'DM Sans', sans-serif; font-weight: 600; font-size: 0.9rem; cursor: pointer;
  transition: background 0.15s;
}
.btn-confirm:hover { background: #134429; }
.btn-feedback {
  background: transparent; color: #1a5c38; border: 2px solid #1a5c38;
  border-radius: 100px; padding: 9px 20px;
  font-family: 'DM Sans', sans-serif; font-weight: 600; font-size: 0.9rem; cursor: pointer;
  transition: all 0.15s;
}
.btn-feedback:hover { background: #1a5c38; color: #fff; }

.feedback-display {
  width: 100%; background: #f9f7f4; border: 1px solid #e8e4df;
  border-radius: 12px; padding: 14px 16px; text-align: left;
}
.fb-display-title {
  font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em;
  color: #8a8a8a; font-weight: 700; margin-bottom: 6px;
}
.fb-display-stars { color: #f4b942; font-size: 1.1rem; letter-spacing: 2px; margin-bottom: 4px; }
.fb-display-text { font-size: 0.9rem; color: #4a4a4a; font-style: italic; }
.fb-display-photo {
  margin-top: 10px; width: 72px; height: 72px; object-fit: cover;
  border-radius: 10px; border: 1px solid #e8e4df; cursor: pointer;
}

/* ─── Feedback modal ─── */
.fb-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 16px;
}
.fb-modal {
  background: #fff; border-radius: 18px; padding: 28px;
  max-width: 440px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  max-height: 90vh; overflow-y: auto;
}
.fb-modal-title {
  font-family: 'Playfair Display', serif; font-size: 1.4rem; font-weight: 700;
  color: #1a1a1a; margin: 0 0 6px;
}
.fb-modal-sub { font-size: 0.88rem; color: #6a6a6a; margin: 0 0 14px; line-height: 1.5; }
.fb-label { font-size: 0.8rem; font-weight: 700; color: #1a1a1a; margin: 14px 0 8px; }
.fb-stars { display: flex; gap: 6px; }
.fb-star { font-size: 1.9rem; color: #dcdcdc; cursor: pointer; line-height: 1; transition: color 0.1s, transform 0.1s; }
.fb-star:hover { transform: scale(1.1); }
.fb-star.on { color: #f4b942; }
.fb-textarea {
  width: 100%; min-height: 90px; border: 1px solid #d8d3cc; border-radius: 12px;
  padding: 12px; font-family: 'DM Sans', sans-serif; font-size: 0.9rem;
  resize: vertical; box-sizing: border-box;
}
.fb-textarea:focus { outline: none; border-color: #1a5c38; }
.fb-file { width: 100%; font-size: 0.85rem; color: #6a6a6a; }
.fb-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 22px; }
.fb-cancel {
  background: #f0ede9; color: #4a4a4a; border: none; border-radius: 100px;
  padding: 11px 22px; font-family: 'DM Sans', sans-serif; font-weight: 600; cursor: pointer;
}
.fb-submit {
  background: #1a5c38; color: #fff; border: none; border-radius: 100px;
  padding: 11px 24px; font-family: 'DM Sans', sans-serif; font-weight: 600; cursor: pointer;
}
.fb-submit:disabled, .fb-cancel:disabled { opacity: 0.6; cursor: not-allowed; }

/* Responsive */
@media (max-width: 700px) {
  .myorders-inner { padding: 0 14px; }
  .myorders-title { font-size: 1.5rem; }
  .order-footer { grid-template-columns: 1fr; gap: 14px; }
  .order-summary { text-align: left; }
  .order-products-grid { grid-template-columns: 1fr; }
  .myorders-empty { padding: 36px 22px; border-radius: 14px; }
  .myorders-actions { flex-direction: column; }
  .btn-primary-green, .btn-outline-green { width: 100%; }
}

/* ─── Repeat orders (reorder + subscriptions) ─── */
.repeat-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e8e4df; }
.btn-reorder, .btn-subscribe {
  border: 1.5px solid #1a5c38; background: #fff; color: #1a5c38; border-radius: 9px;
  padding: 8px 14px; font-size: 0.85rem; font-weight: 700; cursor: pointer; transition: all 0.15s;
}
.btn-reorder:hover, .btn-subscribe:hover { background: #1a5c38; color: #fff; }
.repeat-freq {
  border: 1px solid #e0dcd4; border-radius: 9px; background: #faf8f4; color: #1a1a1a;
  font-size: 0.83rem; padding: 8px 10px; outline: none;
}
.subs-panel { background: #fff; border: 1px solid #e8e4df; border-radius: 14px; padding: 18px 20px; margin-bottom: 22px; }
.subs-title { font-weight: 800; color: #1a1a1a; margin-bottom: 12px; font-size: 1.05rem; }
.subs-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; padding: 10px 0; border-bottom: 1px solid #f1ede6; }
.subs-row:last-child { border-bottom: none; }
.subs-products { font-weight: 600; color: #1a1a1a; font-size: 0.9rem; }
.subs-meta { color: #8a8a8a; font-size: 0.8rem; margin-top: 3px; }
.subs-btns { display: flex; gap: 8px; }
.subs-btns button {
  border: 1px solid #e0dcd4; background: #fff; color: #1a1a1a; border-radius: 8px;
  padding: 6px 12px; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.subs-btns button:hover { border-color: #1a5c38; color: #1a5c38; }
.subs-btns button.danger:hover { border-color: #d94f3d; color: #d94f3d; }

/* ─── Status filter chips ─── */
.order-filters { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 18px; }
.filter-chip {
  border: 1px solid #e0dcd4; background: #fff; color: #1a1a1a; border-radius: 100px;
  padding: 7px 15px; font-size: 0.82rem; font-weight: 600; cursor: pointer; transition: all 0.15s;
  font-family: 'DM Sans', sans-serif;
}
.filter-chip:hover { border-color: #1a5c38; color: #1a5c38; }
.filter-chip.active { background: #1a5c38; border-color: #1a5c38; color: #fff; }
.orders-none { text-align: center; color: #8a8a8a; padding: 40px 0; }

/* ─── Discount breakdown ─── */
.ok-line { color: #16a34a; }

/* ─── Payment + address detail boxes ─── */
.order-extra-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e8e4df;
}
@media (max-width: 640px) { .order-extra-grid { grid-template-columns: 1fr; } }
.order-extra-box {
  background: #faf8f4; border: 1px solid #f1ede6; border-radius: 11px;
  padding: 13px 15px; font-size: 0.84rem; color: #444; line-height: 1.65;
}
.extra-title { font-weight: 800; color: #1a1a1a; margin-bottom: 5px; font-size: 0.88rem; }
.mono { font-family: monospace; font-size: 0.78rem; word-break: break-all; }
.proof-link { display: inline-block; color: #16a34a; font-weight: 700; text-decoration: none; margin-top: 3px; }
.proof-link:hover { text-decoration: underline; color: #15803d; }
.proof-btn {
  display: inline-block; margin-top: 8px; border: 1.5px solid #1a5c38; color: #1a5c38;
  background: #fff; border-radius: 9px; padding: 7px 13px; font-size: 0.82rem;
  font-weight: 700; cursor: pointer; transition: all 0.15s;
}
.proof-btn:hover { background: #1a5c38; color: #fff; }
`;

const Order = () => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Repeat orders: one-click reorder + subscriptions (plan-gated)
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [subs, setSubs]         = useState([]);
  const [subFreq, setSubFreq]   = useState({}); // orderId → frequency days

  // Delivery-confirmation / feedback modal
  const [fbOrder, setFbOrder]       = useState(null);
  const [rating, setRating]         = useState(0);
  const [feedback, setFeedback]     = useState("");
  const [photo, setPhoto]           = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadSubs = () => api.get("/subscriptions").then((r) => setSubs(r.data || [])).catch(() => {});
  useEffect(() => {
    api.get("/subscriptions/status")
      .then((r) => {
        setRepeatEnabled(!!r.data.enabled);
        if (r.data.enabled && localStorage.getItem("authToken")) loadSubs();
      })
      .catch(() => {});
  }, []);

  const handleReorder = async (orderId) => {
    try {
      const r = await api.post(`/subscriptions/reorder/${orderId}`);
      toast.success(t("repeat.reorderOk", { count: r.data.added }));
      setTimeout(() => navigate("/cart"), 800);
    } catch (err) {
      toast.error(err.response?.data?.message || t("repeat.reorderFailed"));
    }
  };
  const handleSubscribe = async (orderId) => {
    try {
      await api.post(`/subscriptions/from-order/${orderId}`, { frequency_days: Number(subFreq[orderId] || 30) });
      toast.success(t("repeat.subscribed"));
      loadSubs();
    } catch (err) {
      toast.error(err.response?.data?.message || t("repeat.subscribeFailed"));
    }
  };
  const handleSubAction = async (id, action) => {
    try {
      await api.put(`/subscriptions/${id}`, { action });
      loadSubs();
    } catch (err) {
      toast.error(err.response?.data?.message || t("orders.submitFailed"));
    }
  };

  // ─── Status filter + payment-proof upload ──────────────────────────────────
  const [statusFilter, setStatusFilter] = useState("all");
  const [proofUploadingId, setProofUploadingId] = useState(null);

  const ORDER_STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];
  const countFor = (s) => (s === "all" ? orders.length : orders.filter((o) => o.status === s).length);
  const visibleOrders = statusFilter === "all" ? orders : orders.filter((o) => o.status === statusFilter);
  const itemsTotal = (order) =>
    (order.products || []).reduce((sum, it) => sum + (it.unit_price || 0) * (it.quantity || 0), 0);

  const uploadProof = async (orderId, file) => {
    if (!file) return;
    setProofUploadingId(orderId);
    try {
      const fd = new FormData();
      fd.append("screenshot", file);
      const res = await api.put(`/orders/${orderId}/upload-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const updated = res.data.order || {};
      setOrders((prev) => prev.map((o) => o._id === orderId
        ? { ...o, confirmation_screenshot: updated.confirmation_screenshot || o.confirmation_screenshot }
        : o));
      toast.success(t("orders.proofOk"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("orders.proofFailed"));
    } finally {
      setProofUploadingId(null);
    }
  };

  const openFeedback = (order) => {
    setFbOrder(order);
    setRating(order.feedback_rating || 0);
    setFeedback(order.customer_feedback || "");
    setPhoto(null);
  };
  const closeFeedback = () => { if (!submitting) setFbOrder(null); };

  const submitFeedback = async () => {
    if (!fbOrder) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (rating) fd.append("rating", String(rating));
      if (feedback.trim()) fd.append("feedback", feedback.trim());
      if (photo) fd.append("photo", photo);
      const res = await api.put(`/orders/${fbOrder._id}/confirm-delivery`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const u = res.data.order || {};
      setOrders((prev) => prev.map((o) => o._id === fbOrder._id ? {
        ...o,
        status: u.status || o.status,
        delivered_at: u.delivered_at || o.delivered_at,
        customer_confirmed: u.customer_confirmed ?? o.customer_confirmed,
        customer_feedback: u.customer_feedback ?? o.customer_feedback,
        feedback_rating: u.feedback_rating ?? o.feedback_rating,
        delivery_photo: u.delivery_photo ?? o.delivery_photo,
      } : o));
      toast.success(fbOrder.status === "shipped"
        ? t("orders.deliveryConfirmedThanks")
        : t("orders.feedbackThanks"));
      setFbOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.message || t("orders.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          setAuthError(true);
          setLoading(false);
          return;
        }
        const res = await api.get("/orders");
        setOrders(res.data || []);
      } catch (err) {
        // 401 = not logged in, otherwise some other error
        if (err.response?.status === 401) {
          setAuthError(true);
        } else {
          toast.error(t("orders.loadFailed"));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (n) => `₩${(n || 0).toLocaleString()}`;
  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  /* ─── Loading ─── */
  if (loading) {
    return (
      <>
        <style>{S}</style>
        <div className="myorders-page">
          <div className="myorders-inner">
            <div className="myorders-loading">
              <div className="myorders-spinner" />
              <span>{t("orders.loading")}</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ─── Not logged in ─── */
  if (authError) {
    return (
      <>
        <style>{S}</style>
        <div className="myorders-page">
          <ToastContainer position="bottom-right" autoClose={3000} />
          <div className="myorders-inner">
            <h1 className="myorders-title">{t("orders.title")}</h1>

            <div className="myorders-empty">
              <span className="myorders-empty-icon">🔐</span>
              <h3>{t("orders.loginTitle")}</h3>
              <p>{t("orders.loginText")}</p>

              <div className="guest-info-box">
                <div className="guest-info-title">
                  🛍️ {t("orders.guestTitle")}
                </div>
                <ul className="guest-info-list">
                  <li>{t("orders.guest1")}</li>
                  <li>{t("orders.guest2")}</li>
                  <li>{t("orders.guest3")}</li>
                  <li>{t("orders.guest4")}</li>
                </ul>
              </div>

              <div className="myorders-actions">
                <Link to="/login" className="btn-primary-green">{t("nav.login")}</Link>
                <Link to="/shop"  className="btn-outline-green">{t("cart.continueShopping")}</Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ─── Logged in but no orders ─── */
  if (!orders || orders.length === 0) {
    return (
      <>
        <style>{S}</style>
        <div className="myorders-page">
          <ToastContainer position="bottom-right" autoClose={3000} />
          <div className="myorders-inner">
            <h1 className="myorders-title">{t("orders.title")}</h1>

            <div className="myorders-empty">
              <span className="myorders-empty-icon">📦</span>
              <h3>{t("orders.emptyTitle")}</h3>
              <p>{t("orders.emptyText")}</p>

              <div className="guest-info-box">
                <div className="guest-info-title">
                  🤔 {t("orders.missingTitle")}
                </div>
                <ul className="guest-info-list">
                  <li>{t("orders.missing1")}</li>
                  <li>{t("orders.missing2")}</li>
                  <li>{t("orders.missing3")}</li>
                </ul>
              </div>

              <div className="myorders-actions">
                <Link to="/shop" className="btn-primary-green">{t("orders.startShopping")}</Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ─── Order list ─── */
  return (
    <>
      <style>{S}</style>
      <div className="myorders-page">
        <ToastContainer position="bottom-right" autoClose={3000} />
        <div className="myorders-inner">
          <h1 className="myorders-title">{t("orders.title")}</h1>

          {/* ─── My subscriptions ─── */}
          {repeatEnabled && subs.length > 0 && (
            <div className="subs-panel">
              <div className="subs-title">🔁 {t("repeat.mySubs")}</div>
              {subs.map((s) => (
                <div className="subs-row" key={s._id}>
                  <div>
                    <div className="subs-products">
                      {(s.products || []).map((p) => p.product_id?.name || "…").join(", ")}
                    </div>
                    <div className="subs-meta">
                      {t("repeat.everyN", { days: s.frequency_days })} · {t("repeat.next")}: {new Date(s.next_run).toLocaleDateString()}
                      {!s.active && ` · ⏸ ${t("repeat.paused")}`}
                    </div>
                  </div>
                  <div className="subs-btns">
                    {s.active
                      ? <button onClick={() => handleSubAction(s._id, "pause")}>{t("repeat.pause")}</button>
                      : <button onClick={() => handleSubAction(s._id, "resume")}>{t("repeat.resume")}</button>}
                    <button className="danger" onClick={() => handleSubAction(s._id, "cancel")}>{t("orders.cancel")}</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ─── Status filter ─── */}
          <div className="order-filters">
            {["all", ...ORDER_STATUSES.filter((s) => countFor(s) > 0)].map((s) => (
              <button
                key={s}
                className={`filter-chip ${statusFilter === s ? "active" : ""}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === "all" ? t("orders.all") : s} ({countFor(s)})
              </button>
            ))}
          </div>

          {visibleOrders.length === 0 && (
            <div className="orders-none">{t("orders.noneWithStatus")}</div>
          )}

          {visibleOrders.map((order) => (
            <div className="order-card" key={order.order_id || order._id}>
              <div className="order-header">
                <div>
                  <span className="order-id-label">{t("orders.orderId")}</span>
                  <span className="order-id-value">{order.order_id}</span>
                </div>
                <span className={`order-badge badge-${order.status}`}>
                  {order.status}
                </span>
              </div>

              <div className="order-body">
                <div className="order-products-grid">
                  {order.products?.map((item, idx) => (
                    <div className="order-product" key={idx}>
                      <img
                        src={item.productImageUrl || "/images/default-product.jpg"}
                        alt={item.product_id?.name || "Product"}
                        className="order-product-img"
                        onError={e => { e.target.onerror = null; e.target.src = "/images/default-product.jpg"; }}
                      />
                      <div className="order-product-info">
                        <div className="order-product-name">
                          {item.product_id?.name || "Product"}
                        </div>
                        <div className="order-product-meta">
                          {t("cart.quantity")}: {item.quantity} × {formatCurrency(item.unit_price)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-footer">
                  <div className="order-meta-list">
                    <div>
                      <strong>{t("orders.payment")}:</strong> {order.payment_method}
                      <span className={`pay-badge pay-${order.payment_status}`}>
                        {order.payment_status}
                      </span>
                    </div>
                    <div><strong>{t("orders.placed")}:</strong> {formatDate(order.placed_at)}</div>
                    {order.tracking_number && (
                      <div><strong>{t("orders.tracking")}:</strong> {order.tracking_number}</div>
                    )}
                  </div>

                  <div className="order-summary">
                    <div><strong>{t("orders.itemsTotal")}:</strong> {formatCurrency(itemsTotal(order))}</div>
                    {order.sale_discount > 0 && (
                      <div className="ok-line"><strong>{t("orders.saleDiscount")}:</strong> -{formatCurrency(order.sale_discount)}</div>
                    )}
                    {order.promo_discount > 0 && (
                      <div className="ok-line">
                        <strong>{t("orders.promoDiscount")}{order.coupon_code ? ` (${order.coupon_code})` : ""}:</strong> -{formatCurrency(order.promo_discount)}
                      </div>
                    )}
                    {order.points_discount > 0 && (
                      <div className="ok-line">
                        <strong>{t("orders.pointsUsed", { pts: order.points_redeemed || 0 })}:</strong> -{formatCurrency(order.points_discount)}
                      </div>
                    )}
                    <div><strong>{t("cart.shipping")}:</strong> {order.shipping_price > 0 ? formatCurrency(order.shipping_price) : t("orders.free")}</div>
                    <div className="order-total">
                      <strong>{t("cart.total")}:</strong> {formatCurrency(order.total_price)}
                    </div>
                  </div>
                </div>

                {/* ─── Payment details + delivery address ─── */}
                <div className="order-extra-grid">
                  <div className="order-extra-box">
                    <div className="extra-title">💳 {t("orders.payment")}</div>
                    {order.payment_method === "inicis_card" ? (
                      <>
                        {order.paid_at && <div>{t("orders.paidAt")}: {formatDate(order.paid_at)}</div>}
                        {order.inicis_tid && (
                          <div className="mono">{t("orders.txnId")}: {order.inicis_tid}</div>
                        )}
                      </>
                    ) : (
                      <>
                        {order.depositor_name && (
                          <div>{t("orders.depositor")}: {order.depositor_name}</div>
                        )}
                        {order.paid_at && <div>{t("orders.paidAt")}: {formatDate(order.paid_at)}</div>}
                        {order.confirmation_screenshot && (
                          <a className="proof-link" href={order.confirmation_screenshot}
                             target="_blank" rel="noreferrer">
                            ✓ {t("orders.proofUploaded")}
                          </a>
                        )}
                        {order.payment_status === "unpaid" && (
                          <label className="proof-btn">
                            {proofUploadingId === order._id
                              ? t("orders.submitting")
                              : (order.confirmation_screenshot ? t("orders.replaceProof") : t("orders.uploadProof"))}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              disabled={proofUploadingId === order._id}
                              onChange={(e) => { uploadProof(order._id, e.target.files?.[0]); e.target.value = ""; }}
                            />
                          </label>
                        )}
                      </>
                    )}
                  </div>
                  {order.shipping_address_id && (
                    <div className="order-extra-box">
                      <div className="extra-title">📍 {t("orders.deliveryAddress")}</div>
                      <div>{order.shipping_address_id.full_name}</div>
                      <div>
                        {[
                          order.shipping_address_id.street || order.shipping_address_id.address_line || order.shipping_address_id.address_details,
                          order.shipping_address_id.city,
                          order.shipping_address_id.postal_code,
                        ].filter(Boolean).join(", ")}
                      </div>
                      {order.shipping_address_id.phone && <div>📞 {order.shipping_address_id.phone}</div>}
                    </div>
                  )}
                </div>

                {/* ─── Reorder + subscribe (plan-gated) ─── */}
                {repeatEnabled && order.status !== "cancelled" && (
                  <div className="repeat-actions">
                    <button className="btn-reorder" onClick={() => handleReorder(order._id)}>
                      ↻ {t("repeat.reorder")}
                    </button>
                    {!subs.some((s) => String(s.source_order_id) === String(order._id)) && (
                      <>
                        <select
                          className="repeat-freq"
                          value={subFreq[order._id] || 30}
                          onChange={(e) => setSubFreq((p) => ({ ...p, [order._id]: e.target.value }))}
                        >
                          <option value={7}>{t("repeat.every7")}</option>
                          <option value={14}>{t("repeat.every14")}</option>
                          <option value={30}>{t("repeat.every30")}</option>
                        </select>
                        <button className="btn-subscribe" onClick={() => handleSubscribe(order._id)}>
                          🔁 {t("repeat.subscribe")}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {order.status !== "cancelled" && (
                  <div className="order-actions">
                    {order.customer_feedback || order.feedback_rating || order.delivery_photo ? (
                      <div className="feedback-display">
                        <div className="fb-display-title">{t("orders.yourFeedback")}</div>
                        {order.feedback_rating > 0 && (
                          <div className="fb-display-stars">
                            {"★".repeat(order.feedback_rating)}{"☆".repeat(5 - order.feedback_rating)}
                          </div>
                        )}
                        {order.customer_feedback && (
                          <div className="fb-display-text">"{order.customer_feedback}"</div>
                        )}
                        {order.delivery_photo && (
                          <a href={order.delivery_photo} target="_blank" rel="noreferrer">
                            <img src={order.delivery_photo} alt="Delivery" className="fb-display-photo" />
                          </a>
                        )}
                      </div>
                    ) : order.status === "shipped" ? (
                      <button className="btn-confirm" onClick={() => openFeedback(order)}>
                        ✓ {t("orders.confirmDeliveryFeedback")}
                      </button>
                    ) : order.status === "delivered" ? (
                      <button className="btn-feedback" onClick={() => openFeedback(order)}>
                        ★ {t("orders.leaveFeedback")}
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {fbOrder && (
        <div className="fb-modal-overlay" onClick={closeFeedback}>
          <div className="fb-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="fb-modal-title">
              {fbOrder.status === "shipped" ? t("orders.confirmDelivery") : t("orders.leaveFeedback")}
            </h3>
            <p className="fb-modal-sub">
              {fbOrder.status === "shipped"
                ? t("orders.confirmSub")
                : t("orders.feedbackSub")}
            </p>

            <div className="fb-label">{t("orders.rating")}</div>
            <div className="fb-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <span
                  key={n}
                  className={`fb-star ${n <= rating ? "on" : ""}`}
                  onClick={() => setRating(n)}
                  role="button"
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >★</span>
              ))}
            </div>

            <div className="fb-label">{t("orders.feedbackOptional")}</div>
            <textarea
              className="fb-textarea"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t("orders.feedbackPlaceholder")}
              maxLength={2000}
            />

            <div className="fb-label">{t("orders.photoOptional")}</div>
            <input
              type="file"
              accept="image/*"
              className="fb-file"
              onChange={(e) => setPhoto(e.target.files[0] || null)}
            />

            <div className="fb-actions">
              <button className="fb-cancel" onClick={closeFeedback} disabled={submitting}>
                {t("orders.cancel")}
              </button>
              <button className="fb-submit" onClick={submitFeedback} disabled={submitting}>
                {submitting
                  ? t("orders.submitting")
                  : fbOrder.status === "shipped" ? t("orders.confirmDelivery") : t("orders.submitFeedback")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Order;